import React, { useState, useEffect } from 'react';
import { getRooms, getTables, createRoom, createTable, updateTable, deleteTable, deleteRoom } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  UtensilsCrossed, Plus, Pencil, Trash2, Loader2, RefreshCw, Users, Home, X
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminTables() {
  const [rooms, setRooms] = useState([]);
  const [tables, setTables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [newRoomName, setNewRoomName] = useState('');
  const [tableForm, setTableForm] = useState({ name: '', sort_order: 0, room_id: '', capacity: 4, shape: 'square' });
  const [subGroups, setSubGroups] = useState([]);
  const [newSubGroupName, setNewSubGroupName] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [roomsData, tablesData] = await Promise.all([
        getRooms(),
        getTables()
      ]);
      setRooms(roomsData);
      setTables(tablesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      toast.error('Bitte Raumnamen eingeben');
      return;
    }
    try {
      await createRoom(newRoomName, rooms.length);
      toast.success('Raum erstellt');
      setShowRoomDialog(false);
      setNewRoomName('');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    const roomTables = tables.filter(t => t.room_id === roomId);
    if (roomTables.length > 0) {
      toast.error('Raum hat noch Tische');
      return;
    }
    if (!window.confirm('Raum wirklich löschen?')) return;
    
    try {
      await deleteRoom(roomId);
      toast.success('Raum gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleOpenTableDialog = (table = null) => {
    if (table) {
      setEditingTable(table);
      setTableForm({
        name: table.name,
        sort_order: table.sort_order ?? 0,
        room_id: table.room_id,
        capacity: table.capacity,
        shape: table.shape || 'square'
      });
      setSubGroups(table.sub_groups || []);
    } else {
      setEditingTable(null);
      setTableForm({ name: '', sort_order: 0, room_id: rooms[0]?.id || '', capacity: 4, shape: 'square' });
      setSubGroups([]);
    }
    setNewSubGroupName('');
    setShowTableDialog(true);
  };

  const addSubGroup = () => {
    const name = newSubGroupName.trim();
    if (!name) return;
    setSubGroups(prev => [...prev, { id: `sg-${Date.now()}`, name, seats: 1 }]);
    setNewSubGroupName('');
  };

  const removeSubGroup = (id) => setSubGroups(prev => prev.filter(sg => sg.id !== id));

  const handleSaveTable = async () => {
    if (!tableForm.name.trim() || !tableForm.room_id) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    try {
      const payload = { ...tableForm, sub_groups: subGroups };
      if (editingTable) {
        await updateTable(editingTable.id, payload);
        toast.success('Tisch aktualisiert');
      } else {
        await createTable(payload);
        toast.success('Tisch erstellt');
      }
      setShowTableDialog(false);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Tisch wirklich löschen?')) return;
    try {
      await deleteTable(tableId);
      toast.success('Tisch gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getTablesByRoom = (roomId) => tables.filter(t => t.room_id === roomId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-tables">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Tische & Räume</h1>
          <p className="text-slate-500">{rooms.length} Räume, {tables.length} Tische</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button variant="outline" onClick={() => setShowRoomDialog(true)}>
            <Home className="w-4 h-4 mr-2" />
            Neuer Raum
          </Button>
          <Button 
            className="bg-[#1a2f38] hover:bg-[#2a4048]"
            onClick={() => handleOpenTableDialog()}
            data-testid="add-table-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Tisch
          </Button>
        </div>
      </div>

      {rooms.map((room) => (
        <Card key={room.id} className="shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5" />
                {room.name}
                <span className="text-sm font-normal text-slate-500">
                  ({getTablesByRoom(room.id).length} Tische)
                </span>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600"
                onClick={() => handleDeleteRoom(room.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {getTablesByRoom(room.id).map((table) => (
                <div
                  key={table.id}
                  className={`p-4 rounded-lg border-2 ${
                    table.status === 'free' ? 'bg-green-50 border-green-200' :
                    table.status === 'occupied' ? 'bg-orange-50 border-orange-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">
                      {table.sort_order ? <span className="text-slate-400 font-normal mr-1">#{table.sort_order}</span> : null}
                      {table.name}
                    </h3>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleOpenTableDialog(table)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 text-red-500"
                        onClick={() => handleDeleteTable(table.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <Users className="w-4 h-4" />
                    <span>{table.capacity} Plätze</span>
                  </div>
                </div>
              ))}
              {getTablesByRoom(room.id).length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-400">
                  Keine Tische in diesem Raum
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {rooms.length === 0 && (
        <div className="text-center py-12">
          <Home className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Keine Räume vorhanden</p>
          <Button 
            onClick={() => setShowRoomDialog(true)} 
            className="mt-4 bg-[#1a2f38]"
          >
            Ersten Raum erstellen
          </Button>
        </div>
      )}

      {/* Room Dialog */}
      <Dialog open={showRoomDialog} onOpenChange={setShowRoomDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuen Raum erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Raumname</Label>
              <Input
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="z.B. Terrasse"
              />
            </div>
            <Button onClick={handleCreateRoom} className="w-full bg-[#1a2f38]">
              Erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Table Dialog */}
      <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTable ? 'Tisch bearbeiten' : 'Neuen Tisch erstellen'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Tischname *</Label>
                <Input
                  value={tableForm.name}
                  onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
                  placeholder="z.B. Tisch 1"
                />
              </div>
              <div>
                <Label>Nummer</Label>
                <Input
                  type="number"
                  min={0}
                  value={tableForm.sort_order}
                  onChange={(e) => setTableForm({ ...tableForm, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <Label>Raum *</Label>
              <Select
                value={tableForm.room_id}
                onValueChange={(value) => setTableForm({ ...tableForm, room_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Raum wählen" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map(room => (
                    <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kapazität</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={tableForm.capacity}
                  onChange={(e) => setTableForm({ ...tableForm, capacity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label>Form</Label>
                <Select
                  value={tableForm.shape}
                  onValueChange={(value) => setTableForm({ ...tableForm, shape: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Quadrat</SelectItem>
                    <SelectItem value="round">Rund</SelectItem>
                    <SelectItem value="rectangle">Rechteck</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Sub-groups */}
            <div>
              <Label>Untergruppen (für Splitbilling)</Label>
              <div className="mt-1 space-y-1">
                {subGroups.map(sg => (
                  <div key={sg.id} className="flex items-center gap-2">
                    <span className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded px-3 py-1">{sg.name}</span>
                    <button onClick={() => removeSubGroup(sg.id)} className="text-slate-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newSubGroupName}
                  onChange={e => setNewSubGroupName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addSubGroup()}
                  placeholder="z.B. Platz A"
                  className="flex-1 text-sm"
                />
                <Button type="button" variant="outline" onClick={addSubGroup} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Button onClick={handleSaveTable} className="w-full bg-[#1a2f38]">
              {editingTable ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
